import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit, Save, X } from 'lucide-react'
import { fetchPlanById, updatePlan } from '../services/api'
import Loader from '../components/Loader'

const PlanDetails = () => {
	const { id } = useParams()
	const navigate = useNavigate()
	const [plan, setPlan] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const [isEditing, setIsEditing] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [error, setError] = useState('')
	const [formData, setFormData] = useState({})
	const [contractFile, setContractFile] = useState(null)

	useEffect(() => {
		loadPlan()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id])

	const loadPlan = async () => {
		setIsLoading(true)
		setError('')
		
		try {
			const response = await fetchPlanById(id)
			setPlan(response)
			setFormData(response)
		} catch (err) {
			setError(err.message)
		} finally {
			setIsLoading(false)
		}
	}

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target
		setFormData(prev => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value
		}))
	}

	const handleFileChange = (e) => {
		setContractFile(e.target.files[0])
	}

	const handleEdit = () => {
		setIsEditing(true)
		setError('')
	}

	const handleCancel = () => {
		setIsEditing(false)
		setFormData(plan)
		setContractFile(null)
		setError('')
	}

	const handleSave = async () => {
		setIsSaving(true)
		setError('')

		try {
			const formDataToSend = new FormData()
			
			// Append all form fields
			Object.keys(formData).forEach(key => {
				if (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined) {
					formDataToSend.append(key, formData[key])
				}
			})

			// Append contract file if updated
			if (contractFile) {
				formDataToSend.append('contract', contractFile)
			}

			await updatePlan(id, formDataToSend)
			setIsEditing(false)
			loadPlan() // Refresh the plan data
		} catch (err) {
			setError(err.message)
		} finally {
			setIsSaving(false)
		}
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<Loader size="lg" />
			</div>
		)
	}

	if (error) {
		return (
			<div className="text-center py-12">
				<div className="text-red-600 text-lg font-medium mb-4">Error loading plan</div>
				<div className="text-gray-600 mb-4">{error}</div>
				<button
					onClick={() => navigate('/plans')}
					className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
				>
					<ArrowLeft className="h-4 w-4 mr-2" />
					Back to Plans
				</button>
			</div>
		)
	}

	if (!plan) {
		return (
			<div className="text-center py-12">
				<div className="text-gray-600 text-lg font-medium mb-4">Plan not found</div>
				<button
					onClick={() => navigate('/plans')}
					className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
				>
					<ArrowLeft className="h-4 w-4 mr-2" />
					Back to Plans
				</button>
			</div>
		)
	}

	return (
		<div>
			<div className="mb-6">
				<button
					onClick={() => navigate('/plans')}
					className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
				>
					<ArrowLeft className="h-4 w-4 mr-2" />
					Back to Plans
				</button>
			</div>

			<div className="bg-white shadow overflow-hidden sm:rounded-lg">
				<div className="px-4 py-5 sm:px-6 flex justify-between items-center">
					<div>
						<h3 className="text-lg leading-6 font-medium text-gray-900">
							Plan Details
						</h3>
						<p className="mt-1 max-w-2xl text-sm text-gray-500">
							View and edit plan information
						</p>
					</div>
					<div className="flex space-x-3">
						{!isEditing ? (
							<button
								onClick={handleEdit}
								className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
							>
								<Edit className="h-4 w-4 mr-2" />
								Edit
							</button>
						) : (
							<>
								<button
									onClick={handleCancel}
									className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
								>
									<X className="h-4 w-4 mr-2" />
									Cancel
								</button>
								<button
									onClick={handleSave}
									disabled={isSaving}
									className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
								>
									{isSaving ? <Loader size="sm" /> : <Save className="h-4 w-4 mr-2" />}
									Save
								</button>
							</>
						)}
					</div>
				</div>

				<div className="border-t border-gray-200">
					<dl>
						{error && (
							<div className="px-4 py-3 bg-red-50 border-l-4 border-red-400">
								<div className="text-sm text-red-700">{error}</div>
							</div>
						)}

						{/* Basic Information */}
						<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
							<dt className="text-sm font-medium text-gray-500">Name</dt>
							<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
								{isEditing ? (
									<input
										type="text"
										name="name"
										value={formData.name || ''}
										onChange={handleChange}
										className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
									/>
								) : (
									plan.name
								)}
							</dd>
						</div>

						<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-gray-50">
							<dt className="text-sm font-medium text-gray-500">Type Account</dt>
							<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
								{isEditing ? (
									<select
										name="typeAccount"
										value={formData.typeAccount || ''}
										onChange={handleChange}
										className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
									>
										<option value="individual">Individual</option>
										<option value="business">Business</option>
									</select>
								) : (
									plan.typeAccount
								)}
							</dd>
						</div>

						<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
							<dt className="text-sm font-medium text-gray-500">Description</dt>
							<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
								{isEditing ? (
									<textarea
										name="description"
										rows={3}
										value={formData.description || ''}
										onChange={handleChange}
										className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
									/>
								) : (
									plan.description
								)}
							</dd>
						</div>

						<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-gray-50">
							<dt className="text-sm font-medium text-gray-500">Benefits</dt>
							<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
								{isEditing ? (
									<textarea
										name="benefits"
										rows={3}
										value={formData.benefits || ''}
										onChange={handleChange}
										className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
									/>
								) : (
									plan.benefits || 'No benefits specified'
								)}
							</dd>
						</div>

						{/* Settings */}
						<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
							<dt className="text-sm font-medium text-gray-500">Availability</dt>
							<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
								{isEditing ? (
									<select
										name="availability"
										value={formData.availability || ''}
										onChange={handleChange}
										className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
									>
										<option value="available">Available</option>
										<option value="unavailable">Unavailable</option>
									</select>
								) : (
									<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
										plan.availability === 'available' 
											? 'bg-green-100 text-green-800' 
											: 'bg-red-100 text-red-800'
									}`}>
										{plan.availability}
									</span>
								)}
							</dd>
						</div>

						<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-gray-50">
							<dt className="text-sm font-medium text-gray-500">Visibility</dt>
							<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
								{isEditing ? (
									<select
										name="visibility"
										value={formData.visibility || ''}
										onChange={handleChange}
										className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
									>
										<option value="public">Public</option>
										<option value="private">Private</option>
									</select>
								) : (
									<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
										plan.visibility === 'public' 
											? 'bg-blue-100 text-blue-800' 
											: 'bg-gray-100 text-gray-800'
									}`}>
										{plan.visibility}
									</span>
								)}
							</dd>
						</div>

						{/* Pricing */}
						<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
							<dt className="text-sm font-medium text-gray-500">Price</dt>
							<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
								{isEditing ? (
									<input
										type="number"
										step="0.01"
										name="price"
										value={formData.price || ''}
										onChange={handleChange}
										className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
									/>
								) : (
									`R$ ${plan.price || '0.00'}`
								)}
							</dd>
						</div>

						<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-gray-50">
							<dt className="text-sm font-medium text-gray-500">PIX Price</dt>
							<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
								{isEditing ? (
									<input
										type="number"
										step="0.01"
										name="pricePix"
										value={formData.pricePix || ''}
										onChange={handleChange}
										className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
									/>
								) : (
									`R$ ${plan.pricePix || '0.00'}`
								)}
							</dd>
						</div>

						{/* Contract File */}
						<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
							<dt className="text-sm font-medium text-gray-500">Contract</dt>
							<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
								{isEditing ? (
									<input
										type="file"
										accept=".pdf"
										onChange={handleFileChange}
										className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
									/>
								) : (
									plan.contract ? (
										<a 
											href={plan.contract} 
											target="_blank" 
											rel="noopener noreferrer"
											className="text-primary-600 hover:text-primary-500"
										>
											View Contract PDF
										</a>
									) : (
										'No contract uploaded'
									)
								)}
							</dd>
						</div>

						{/* Default Plan */}
						<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-gray-50">
							<dt className="text-sm font-medium text-gray-500">Default Plan</dt>
							<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
								{isEditing ? (
									<input
										type="checkbox"
										name="planDefault"
										checked={formData.planDefault || false}
										onChange={handleChange}
										className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
									/>
								) : (
									plan.planDefault ? 'Yes' : 'No'
								)}
							</dd>
						</div>
					</dl>
				</div>
			</div>
		</div>
	)
}

export default PlanDetails
